/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 14:55:08
 *
 *******************************************************/
// @flow
//
import _ from 'lodash';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import {
  Card,
  SkeletonBodyText,
  ResourceList,
  ResourceItem,
  TextStyle,
  Button,
  Badge,
  Stack,
  Icon,
  Tooltip,
  ButtonGroup
} from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { routes } from 'src/routes';
import history from 'src/routes/history';
import ConfirmRevertThemeModal from './confirm-revert-theme-modal';
import PopoverActionButton from 'src/components/views/theme-setup/popover-action-button';
import RevertThemeAllFeatureChoosingModal from 'src/components/views/theme-revert/theme-revert-form/revert-theme-all-feature-survey-modal';
import ContactOurTeamThemeRevertModal from 'src/components/views/theme-revert/theme-revert-form/contact-our-team-theme-revert-modal';
import FeedbackModal from './feedback-modal';
import ThemeRevertErrorModal from 'src/components/containers/common/theme-revert-error-modal';

import { getThemeReviewUrl } from 'src/helpers';
import { getLiveThemeStatusHtml, getLiveThemeStatus } from 'src/helpers/theme-setup-helper';
import { getDateTime } from 'src/helpers/time-helper';
import empty from 'src/assets/images/filter-tree/empty-option.png';
import * as themeSetupAction from 'actions/theme-setup-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import * as syncActions from 'actions/sync-actions';
import { site as siteConfig } from 'src/config';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import { getShop } from 'src/helpers';
import { checkUserEnableShowAppVersionSwitching } from 'src/helpers/account-helper';
import { isThemeTurbo } from 'src/helpers/theme-setup-helper';

type Props = {
  getThemes: () => Promise<void>,
  trackEvent: (eventSource: string, eventName: string, label: string) => Promise<void>,
  getChecklist: () => Promise<void>,
  // Sync for Refresh button
  getFlowSyncThemeStatus: () => Promise<void>,
  startFlowSyncTheme: () => Promise<void>,
  resetSyncStatus: () => void,

  getThemesLoading: boolean,
  themes: Array<Object>,
  flow: Object,
  syncProcessLoading: boolean
};

type ComposedProps = Props & WithI18nProps;
type State = {
  activeModalConfirmSetup: boolean,
  activeFeedbackModal: boolean,
  activeModalError: boolean,
  activeModalRevertThemeAllFeatureChoosing: boolean,
  activeModalContactOurTeamThemeRevert: boolean,
  dismissFlagAll: boolean,
  dismissFlagSearch: boolean,
  dataSetup: Object
};

class Themes extends Component<ComposedProps, State> {
  timeoutGetSyncProcessStatus = undefined;
  state = {
    activeModalConfirmSetup: false,
    activeFeedbackModal: false,
    activeModalError: false,
    dismissFlagAll: false,
    dismissFlagSearch: false,
    dataSetup: {},
    activeModalRevertThemeAllFeatureChoosing: false,
    activeModalContactOurTeamThemeRevert: false
  };

  componentDidMount() {
    const { getThemes } = this.props;
    getThemes();

    let dismissAll = false;
    let dismissSearch = false;
    try {
      dismissAll = localStorage.getItem(`${getShop()}-dismissThemeRevertFeedback-All`);
    } catch (error) {}
    try {
      dismissSearch = localStorage.getItem(`${getShop()}-dismissThemeRevertFeedback-Search`);
    } catch (error) {}

    // If the banner All is dismissed, hide it
    if (dismissAll === 'true') {
      this.setState({ dismissFlagAll: true });
    }

    // If the banner Search is dismissed, hide it
    if (dismissSearch === 'true') {
      this.setState({ dismissFlagSearch: true });
    }
  }

  componentWillReceiveProps(nextProps) {
    const { revertThemeLoading, revertThemeSuccess, revertThemeError } = nextProps;
    const { revertThemeLoading: prevRevertThemeLoading } = this.props;

    // Show feedback modal if success
    if (!revertThemeLoading && prevRevertThemeLoading && revertThemeSuccess) {
      setTimeout(() => {
        this.setState({ activeFeedbackModal: true });
      }, 3000);
    }

    // Show error modal if failure
    if (!revertThemeLoading && prevRevertThemeLoading && revertThemeError !== undefined) {
      this.setState({ activeModalError: true, activeModalConfirmSetup: false });
    }

    if (
      nextProps.revertThemeManualContactSuccess &&
      this.props.revertThemeManualContactSuccess !== nextProps.revertThemeManualContactSuccess
    ) {
      this.setState({ activeModalContactOurTeamThemeRevert: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { flow: prevFlow, syncProcessLoading: prevSyncProcessLoading } = prevProps;
    const {
      flow: currentFlow,
      syncProcessLoading: currentSyncProcessLoading,
      getThemes,
      getFlowSyncThemeStatus,
      i18n
    } = this.props;
    const { status: prevFlowStatus } = prevFlow;
    const { status: currentFlowStatus } = currentFlow;

    if (prevSyncProcessLoading && !currentSyncProcessLoading) {
      // If first getFlowSyncThemeStatus() fails -> flowStatus will be unchanged
      if (currentFlowStatus !== 'running' && prevFlowStatus === currentFlowStatus) {
        // Call getFlowSyncThemeStatus() again
        getFlowSyncThemeStatus();
        return;
      }

      switch (currentFlowStatus) {
        case 'done':
          getThemes();
          break;
        case 'error':
        case 'locked':
        case 'pending':
          showSABToast(i18n.translate('AdminHTML.ThemeSetup.Themes.errorMessage'), true);
          break;
        default:
          this.timeoutGetSyncProcessStatus = setTimeout(() => {
            getFlowSyncThemeStatus();
          }, siteConfig.syncStatusTimeout);
          break;
      }
    }
  }

  componentWillUnmount() {
    const { resetSyncStatus } = this.props;
    resetSyncStatus();

    clearTimeout(this.timeoutGetSyncProcessStatus);
  }

  handleRefresh = async () => {
    const { startFlowSyncTheme, getFlowSyncThemeStatus, i18n } = this.props;
    await startFlowSyncTheme();
    await getFlowSyncThemeStatus();
    showSABToast(i18n.translate('AdminHTML.ThemeSetup.Themes.infoMessage'), false);
  };

  render() {
    const { themes = [], getThemesLoading, flow, i18n, enableAppVersionSwitching } = this.props;
    const {
      activeModalConfirmSetup,
      activeFeedbackModal,
      activeModalError,
      activeModalRevertThemeAllFeatureChoosing,
      activeModalContactOurTeamThemeRevert,
      dataSetup,
      dismissFlagAll,
      dismissFlagSearch
    } = this.state;
    const { status: flowStatus } = flow;
    const loading = getThemesLoading || flowStatus === 'running';

    const isHasDataInDataSetup = _.get(dataSetup, 'theme', false);
    const removeAll = _.get(dataSetup, 'removeAll', false);
    const isShowableActiveFeedbackModal =
      isHasDataInDataSetup &&
      activeFeedbackModal &&
      ((removeAll && !dismissFlagAll) || (!removeAll && !dismissFlagSearch));

    // Get the main theme by published status
    const mainTheme = themes.find(theme => theme.published) || themes[0] || {};

    // Get live theme status
    const status = getLiveThemeStatus(loading, mainTheme);

    return (
      <Card>
        <Card.Section>
          {loading ? (
            <SkeletonBodyText lines={1} />
          ) : (
            <Stack>
              <Stack.Item fill>{getLiveThemeStatusHtml(status, i18n)}</Stack.Item>
              <Stack.Item>
                <div id="BC-Button__Theme-Setup-Refresh">
                  <Button plain disabled={loading} onClick={this.handleRefresh}>
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ThemeSetup.Themes.button')}
                    </TextStyle>
                  </Button>
                  <Tooltip
                    content={i18n.translate('AdminHTML.ThemeSetup.Themes.tooltip')}
                    preferredPosition="above">
                    <Icon source={QuestionMarkMajor} color="base" />
                  </Tooltip>
                </div>
              </Stack.Item>
            </Stack>
          )}
        </Card.Section>
        <Card.Section>
          {loading ? (
            <ResourceList
              resourceName={{ singular: 'theme', plural: 'themes' }}
              items={[...Array(3).keys()]}
              renderItem={item => {
                return (
                  <ResourceItem id={item}>
                    <SkeletonBodyText lines={2} />
                  </ResourceItem>
                );
              }}
            />
          ) : (
            <div className="Polaris-ResourceList__ResourceListWrapper">
              <ul className="Polaris-ResourceList" aria-live="polite">
                {_.map(themes, (item, index) => {
                  const { id, name, updatedAt, published } = item;
                  return (
                    <li className="Polaris-ResourceItem__ListItem" key={index}>
                      <div className="Polaris-ResourceItem__ItemWrapper">
                        <div className="Polaris-ResourceItem">
                          <div className="Polaris-ResourceItem__Container">
                            <div className="Polaris-ResourceItem__Content">
                              <div className="theme-item">
                                <div className="theme-item-left">
                                  <div>
                                    <TextStyle variation="strong">{name}</TextStyle>
                                    {published && (
                                      <span style={{ marginLeft: '5px' }}>
                                        <Badge status="success">
                                          {i18n.translate(
                                            'AdminHTML.ThemeSetup.ThemeSetup.badgeLive'
                                          )}
                                        </Badge>
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ marginTop: '0.5rem' }}>
                                    {updatedAt && updatedAt !== 0 ? (
                                      <TextStyle variation="subdued">
                                        {i18n.translate(
                                          'AdminHTML.ThemeSetup.ThemeSetup.lastSaved',
                                          {
                                            nt_time: getDateTime(updatedAt)
                                          }
                                        )}
                                      </TextStyle>
                                    ) : (
                                      <TextStyle variation="subdued">
                                        {i18n.translate(
                                          'AdminHTML.ThemeSetup.ThemeSetup.notUpdated'
                                        )}
                                      </TextStyle>
                                    )}
                                  </div>
                                </div>
                                <div className="theme-item-right">
                                  {enableAppVersionSwitching && isThemeTurbo(item) ? (
                                    <ButtonGroup>
                                      <Button
                                        onClick={() => history.push(routes.appPreferences.view())}>
                                        {i18n.translate(
                                          'AdminHTML.ThemeSetup.NewThemeSetup.list_theme_info_upgrade_turbo'
                                        )}
                                      </Button>
                                    </ButtonGroup>
                                  ) : (
                                    <ButtonGroup>
                                      <PopoverActionButton
                                        handleRemove={removeAll =>
                                          this.handleRemove(item, removeAll)
                                        }
                                      />

                                      <Button primary onClick={() => this.handlePreview(id)}>
                                        {i18n.translate('AdminHTML.Common.Action.preview')}
                                      </Button>
                                    </ButtonGroup>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {_.isEmpty(themes) && !loading ? (
            <div className="BC-EmptyTheme__ThemeSetup">
              <img src={empty} alt="Empty" />
              <p>{i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.noTheme')}</p>
            </div>
          ) : (
            ''
          )}
          <ConfirmRevertThemeModal
            dataSetup={dataSetup}
            active={activeModalConfirmSetup}
            handleChange={() => this.toggleChangeState('activeModalConfirmSetup')}
          />
          <FeedbackModal
            active={isShowableActiveFeedbackModal}
            dataSetup={dataSetup}
            handleCloseFeedbackModal={(dismissFlagAll, dismissFlagSearch) =>
              this.handleCloseFeedbackModal(dismissFlagAll, dismissFlagSearch)
            }
          />
          <RevertThemeAllFeatureChoosingModal
            active={activeModalRevertThemeAllFeatureChoosing}
            handleClose={() => this.toggleChangeState('activeModalRevertThemeAllFeatureChoosing')}
            handleRevert={this.handleRevert}
            handleRevertContactOurTeam={() => {
              this.toggleChangeState('activeModalContactOurTeamThemeRevert');
              this.toggleChangeState('activeModalRevertThemeAllFeatureChoosing');
            }}
          />
          <ContactOurTeamThemeRevertModal
            dataSetup={dataSetup}
            active={activeModalContactOurTeamThemeRevert}
            handleClose={() => this.toggleChangeState('activeModalContactOurTeamThemeRevert')}
          />
          <ThemeRevertErrorModal
            active={activeModalError}
            handleChange={() => this.toggleChangeState('activeModalError')}
          />
        </Card.Section>
      </Card>
    );
  }

  // Handle preview filter action
  handlePreview = async (id: string) => {
    const { trackEvent, getChecklist } = this.props;

    const previewUrl = getThemeReviewUrl(id);
    // Call to tracking event
    await trackEvent('themeSetup', 'preview', ' ');
    await getChecklist();

    window.open(previewUrl, '_blank');
  };

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  handleRemove = (theme: Object, removeAll: boolean) => {
    if (removeAll) {
      this.setState({
        activeModalRevertThemeAllFeatureChoosing: true,
        dataSetup: { theme, removeAll }
      });
    } else {
      this.setState({ dataSetup: { theme, removeAll }, activeModalConfirmSetup: true });
    }
  };

  handleRevert = () => {
    this.setState({
      activeModalConfirmSetup: true,
      activeModalRevertThemeAllFeatureChoosing: false
    });
  };

  handleCloseFeedbackModal = (dismissFlagAll: boolean, dismissFlagSearch: boolean) => {
    this.setState({ activeFeedbackModal: false });

    // Insert data to local storage
    if (dismissFlagAll) {
      this.setState({ dismissFlagAll });
      try {
        localStorage.setItem(`${getShop()}-dismissThemeRevertFeedback-All`, 'true');
      } catch (error) {}
    }

    if (dismissFlagSearch) {
      this.setState({ dismissFlagSearch });
      try {
        localStorage.setItem(`${getShop()}-dismissThemeRevertFeedback-Search`, 'true');
      } catch (error) {}
    }
  };
}

const mapStateToProps = (state, props) => {
  const { themeSetup, themeRevert, oauth } = state;
  const tenantApp = _.get(oauth, 'tenantApp', {});
  const enableAppVersionSwitching = checkUserEnableShowAppVersionSwitching(tenantApp);
  const themes = _.get(themeSetup, 'themes', []);

  return {
    enableAppVersionSwitching,
    getThemesLoading: _.get(themeSetup, 'getThemesLoading', true),
    themes,
    flow: _.get(state.sync, 'flow', {}),
    syncProcessLoading: _.get(state.sync, 'syncProcessLoading', false),
    revertThemeSuccess: _.get(themeRevert, 'success', false),
    revertThemeLoading: _.get(themeRevert, 'loading', false),
    revertThemeError: _.get(themeRevert, 'revertThemeError', undefined),
    revertThemeManualContactSuccess: _.get(themeRevert, 'manualContactSuccess', false),
    revertThemeManualContactLoading: _.get(themeRevert, 'manualContactLoading', false),
    revertThemeManualContactError: _.get(themeRevert, 'manualContactError', undefined)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getThemes: () => dispatch(themeSetupAction.getThemes(ownProps.i18n)),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n)),
  // Sync for Refresh button
  getFlowSyncThemeStatus: () => dispatch(syncActions.getSyncProcessStatus('FlowSyncTheme')),
  startFlowSyncTheme: () => dispatch(syncActions.triggerSync('FlowSyncTheme', ownProps.i18n)),
  resetSyncStatus: () => dispatch(syncActions.resetSyncStatus())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(Themes));
